import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type components as AdminComponents } from "@njbsoft/types/admin";
import {
  useAdminDeviceInventory,
  type AdminDeviceInventoryParams,
  type AdminDeviceInventorySortBy,
} from "@/hooks/use-admin-device-inventory";
import { DataTable } from "@/components/data-table";
import { Pagination } from "@/components/pagination";
import { useAdminDeviceInventoryColumns } from "@/components/device-inventory/admin-device-inventory-columns";
import {
  AdminDeviceInventoryFilters,
  type AdminDeviceInventoryFilterValues,
} from "@/components/device-inventory/admin-device-inventory-filters";

type AdminDeviceInventory = AdminComponents["schemas"]["AdminDeviceInventory"];

export const Route = createFileRoute("/_admin/temporary-devices/")({
  validateSearch: (search: Record<string, unknown>) => ({
    org_id: typeof search.org_id === "string" ? search.org_id : undefined,
  }),
  component: AdminDeviceInventoryListPage,
});

const PAGE_SIZE = 20;

function AdminDeviceInventoryListPage() {
  const { t } = useTranslation(["device-inventory", "admin"]);
  const router = useRouter();
  const navigate = useNavigate({ from: Route.fullPath });
  const { org_id: urlOrgId } = Route.useSearch();
  const columns = useAdminDeviceInventoryColumns();

  const [filters, setFilters] = useState<AdminDeviceInventoryFilterValues>(() =>
    urlOrgId ? { org_id: urlOrgId } : {},
  );
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);

  useEffect(() => {
    if ((filters.org_id ?? undefined) !== urlOrgId) {
      void navigate({
        search: (prev: Record<string, unknown>) => ({ ...prev, org_id: filters.org_id }),
        replace: true,
      });
    }
  }, [filters.org_id, urlOrgId, navigate]);

  useEffect(() => {
    setPage(1);
  }, [filters, sorting]);

  const firstSort = sorting[0];
  const sortBy = firstSort?.id as AdminDeviceInventorySortBy | undefined;
  const sortDir = firstSort ? (firstSort.desc ? "desc" : "asc") : undefined;

  const params: AdminDeviceInventoryParams = useMemo(() => {
    const p: AdminDeviceInventoryParams = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
    if (filters.org_id) p.org_id = filters.org_id;
    if (filters.manufacturer_id) p.manufacturer_id = filters.manufacturer_id;
    if (filters.retired_only !== undefined) p.retired_only = filters.retired_only;
    if (sortBy) p.sort_by = sortBy;
    if (sortDir) p.sort_dir = sortDir;
    return p;
  }, [filters.org_id, filters.manufacturer_id, filters.retired_only, sortBy, sortDir, page]);

  const { data, isLoading, isError, error } = useAdminDeviceInventory(params);

  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  function handleRowClick(row: AdminDeviceInventory) {
    router.history.push(`/temporary-devices/${row.id}`);
  }

  const noOrgPicked = !filters.org_id;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-slate-900">{t("device-inventory:list.title")}</h1>

      <AdminDeviceInventoryFilters filters={filters} onChange={setFilters} />

      {noOrgPicked ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
          <h2 className="text-base font-semibold text-slate-700">
            {t("admin:deviceInventory.list.pickOrgTitle")}
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            {t("admin:deviceInventory.list.pickOrgDescription")}
          </p>
        </div>
      ) : isError ? (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          {(error as Error)?.message ?? t("device-inventory:list.empty")}
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data?.items ?? []}
            sorting={sorting}
            onSortingChange={setSorting}
            isLoading={isLoading}
            emptyMessage={t("device-inventory:list.empty")}
            onRowClick={handleRowClick}
          />

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={data?.total ?? 0}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
